import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
// @mui
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
// hooks
import useCollapseDrawer from "../../hooks/useCollapseDrawer";
// config
import { HEADER, NAVBAR } from "../../config";
//
import useAuth from "~src/hooks/useAuth";
import useResponsive from "~src/hooks/useResponsive";
import {
  ChromeExtensionEventType,
  sendChromeExtensionMessage,
} from "~src/utils/chromeExtension";
import { SubscriptionCheck } from "./SubscriptionCheck";
import AppHeader from "./header";
import NavbarVertical from "./navbar/NavbarVertical";
import { cancelAllRequests } from "~src/api/common/api";

// ----------------------------------------------------------------------

type MainStyleProps = {
  collapseClick: boolean;
};

const MainStyle = styled("main", {
  shouldForwardProp: (prop) => prop !== "collapseClick",
})<MainStyleProps>(({ collapseClick, theme }) => ({
  flexGrow: 1,
  paddingTop: HEADER.MOBILE_HEIGHT,
  paddingBottom: HEADER.MOBILE_HEIGHT + 24,
  backgroundColor: theme.palette.grey[50],
  [theme.breakpoints.up("lg")]: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
    transition: theme.transitions.create("margin-left", {
      duration: theme.transitions.duration.shorter,
    }),
    ...(collapseClick && {
      marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
    }),
  },
}));

// ----------------------------------------------------------------------

export default function AppLayout() {
  const { isCollapse, collapseClick } = useCollapseDrawer();
  const isDesktop = useResponsive("up", "lg");
  const { user } = useAuth();
  const location = useLocation();

  const [open, setOpen] = useState(false);

  // Cancel any pending requests on navigation
  useEffect(() => {
    return () => {
      // Clean up any pending requests when navigating
      cancelAllRequests('Navigation change');
    };
  }, [location.pathname]);

  useEffect(() => {
    const logIntoExtension = async (loggedInUser: any) => {
      await sendChromeExtensionMessage({
        event: ChromeExtensionEventType.LOG_IN,
        data: loggedInUser,
      });
    };

    if (user) {
      logIntoExtension(user);
    }
  }, [user]);

  return (
    <Box
      sx={{
        display: { lg: "flex" },
        minHeight: { lg: 1 },
      }}
    >
      <SubscriptionCheck />
      {!isDesktop && (
        <AppHeader
          isCollapse={isCollapse}
          onOpenSidebar={() => setOpen(true)}
        />
      )}

      <NavbarVertical
        isOpenSidebar={open}
        onCloseSidebar={() => setOpen(false)}
      />

      <MainStyle collapseClick={collapseClick}>
        <Outlet />
      </MainStyle>
    </Box>
  );
}
